/**
 * projectNumbers.js - which calendar blocks are missing their Project #, and
 * the real projects the editor offers instead of a typed number.
 *
 * My Schedule / My Project (server/routes/engineer.js) show an engineer the
 * GLOBAL # of what they are booked on, read first off the day's own Project #
 * (manpower_edits.project_number). A block saved without one leaves the
 * engineer guessing, so the grid outlines it in red until somebody picks the
 * project. Time off and the labels that are not jobs at all (holidays,
 * training, the office, a placeholder) never need one and are never flagged.
 *
 * Pure: no React, no fetching. Tested in __tests__/projectNumbers.test.js.
 */
import { isTimeOff } from '../../utils/utilizationFromCalendar';
import { isActiveProject, isProjectRow } from '../../data/projectStatus';
import { ymd } from './manpowerHelpers';

/** Same cap as the server's (routes/manpower.js MAX_PROJECT_NUMBER). */
export const MAX_PROJECT_NUMBER = 40;

/**
 * Labels that are not a job, so have no Project # to miss. Time off (PTO,
 * vacation, holiday, sick, leave, "day off") is isTimeOff's; these are the
 * rest. Word-bounded: "Offline" (office work ON a job) is not "office".
 */
const NON_PROJECT_LABEL =
  /\b(holidays?|trainings?|office|place\s*-?\s*holders?|tbd|bench|admin|internal|meetings?|conference|school|onboarding)\b/i;

/** True for a label that is not a job: time off, a holiday, training, the office, a placeholder... */
export const isNonProjectLabel = (label) => {
  const l = String(label || '').trim();
  return !!l && (isTimeOff(l) || NON_PROJECT_LABEL.test(l));
};

/** True when a booked block should carry a Project # and does not. */
export function needsProjectNumber(label, detail) {
  if (!label || !String(label).trim()) return false;
  if (
    detail &&
    String(detail.status || '')
      .trim()
      .toUpperCase() === 'PTO'
  )
    return false;
  if (isNonProjectLabel(label)) return false;
  return !String((detail && detail.projectNumber) || '').trim();
}

/** The tooltip for a flagged block, in plain words. */
export const MISSING_NUMBER_HINT = 'Missing Project # — engineers see no GLOBAL # on My Schedule / My Project. Click to pick the project.';

// Noon, so a DST change at local midnight cannot skip or repeat a day.
const noon = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};
const addDays = (s, n) => {
  const d = noon(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
};
const isWeekendDay = (s) => {
  const g = noon(s).getDay();
  return g === 0 || g === 6;
};

const splitKey = (k) => {
  const i = k.lastIndexOf('|');
  return [k.slice(0, i), k.slice(i + 1)];
};

/**
 * Every block missing a Project #: one engineer, one label, consecutive days
 * (an unbooked weekend in between does not split it - the grid does the same
 * across a weekend the label covers). Sorted by start date, then engineer.
 * `since` (YYYY-MM-DD, optional) keeps only blocks ending on or after it.
 * Returns [{ engineerName, label, from, to, days }].
 */
export function missingNumberBlocks(edits, details, since = '') {
  const byEngineer = new Map();
  for (const [k, label] of Object.entries(edits || {})) {
    if (!label || !needsProjectNumber(label, details && details[k])) continue;
    const [name, day] = splitKey(k);
    if (!byEngineer.has(name)) byEngineer.set(name, []);
    byEngineer.get(name).push([day, label]);
  }
  const out = [];
  for (const [name, list] of byEngineer) {
    list.sort((a, b) => (a[0] < b[0] ? -1 : 1));
    let cur = null;
    for (const [day, label] of list) {
      if (cur && cur.label === label && bridges(edits, name, cur.to, day)) {
        cur.to = day;
        cur.days += 1;
      } else {
        cur = { engineerName: name, label, from: day, to: day, days: 1 };
        out.push(cur);
      }
    }
  }
  return out
    .filter((b) => !since || b.to >= since)
    .sort((a, b) => (a.from === b.from ? a.engineerName.localeCompare(b.engineerName) : a.from < b.from ? -1 : 1));
}

/** Is `next` the day after `prev`, with only unbooked weekend days between them? */
function bridges(edits, name, prev, next) {
  for (let d = addDays(prev, 1); d < next; d = addDays(d, 1)) {
    if (!isWeekendDay(d) || edits[`${name}|${d}`]) return false;
  }
  return true;
}

/**
 * The days a picked Project # can also go on, besides from..to (which the
 * save itself writes), and only days with `label` and no number yet - a day
 * that already has a number keeps it.
 *   scope 'adjacent'  this engineer's days running on from either end of
 *                     from..to with the same label (an unbooked weekend
 *                     bridges, anything else stops it)
 *   scope 'label'     every engineer's days with that label
 * Returns the 'Name|YYYY-MM-DD' keys.
 */
export function fillCandidates(edits, details, { engineerName, from, to, label, scope }) {
  if (!label || !from || !to || isNonProjectLabel(label)) return [];
  const lacks = (k) => edits[k] === label && needsProjectNumber(label, details && details[k]);
  const inside = (k) => {
    const [name, day] = splitKey(k);
    return name === engineerName && day >= from && day <= to;
  };
  if (scope === 'label') {
    return Object.keys(edits || {})
      .filter((k) => lacks(k) && !inside(k))
      .sort();
  }
  const out = [];
  const walk = (start, step) => {
    let gap = 0;
    for (let d = addDays(start, step); gap <= 2; d = addDays(d, step)) {
      const k = `${engineerName}|${d}`;
      if (edits[k] === label) {
        gap = 0;
        if (lacks(k)) out.push(k);
      } else if (!edits[k] && isWeekendDay(d)) {
        gap += 1;
      } else {
        return;
      }
    }
  };
  walk(from, -1);
  walk(to, 1);
  return out.sort();
}

/* ── The real projects ─────────────────────────────────────────────── */

/**
 * The projects a block can be put under: won jobs (Projects tab rows) with a
 * Global Project Number, one per number, running ones first. Rows from
 * AppContext's allProjects (camelCase). Returns
 * [{ gpn, name, customer, active, haystack }].
 */
export function jobOptions(projects) {
  const seen = new Map();
  for (const p of projects || []) {
    const gpn = String((p && p.globalProjectNumber) || '').trim();
    if (!gpn || !isProjectRow(p)) continue;
    const key = gpn.toLowerCase();
    const active = isActiveProject(p);
    const was = seen.get(key);
    if (was && (was.active || !active)) continue;
    const customer = [p.customer, p.subCustomer]
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .reduce((acc, v) => (acc && acc.toLowerCase().includes(v.toLowerCase()) ? acc : acc ? `${acc} - ${v}` : v), '');
    const name = String(p.name || p.description || '').trim();
    seen.set(key, { gpn, name, customer, active, haystack: `${gpn} ${name} ${customer}`.toLowerCase() });
  }
  return [...seen.values()].sort((a, b) => (a.active !== b.active ? (a.active ? -1 : 1) : b.gpn.localeCompare(a.gpn)));
}

/** "P26053-010 · GM Springhill Process Support Modbuild · FANUC - PSH" */
export const jobCaption = (j) => [j.gpn, j.name, j.customer].filter(Boolean).join(' · ');

/** The project with this number, or null. Case and spaces do not matter. */
export function findJob(options, number) {
  const n = String(number || '')
    .trim()
    .toLowerCase();
  if (!n) return null;
  return (options || []).find((j) => j.gpn.toLowerCase() === n) || null;
}

const IGNORED_WORDS = ['site', 'offline', 'online', 'mb', 'modbuild'];
const words = (s) =>
  String(s || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 2);

/**
 * The projects matching what was typed (every word must appear in the
 * number, name or customer). Nothing typed: the ones sharing words with the
 * block's label come first, so "TESLA" opens on the Tesla jobs.
 */
export function searchJobs(options, query, label = '', limit = 60) {
  const q = words(query);
  if (q.length) {
    const raw = String(query).trim().toLowerCase();
    return (options || []).filter((j) => j.haystack.includes(raw) || q.every((w) => j.haystack.includes(w))).slice(0, limit);
  }
  const lw = words(label).filter((w) => !IGNORED_WORDS.includes(w));
  if (!lw.length) return (options || []).slice(0, limit);
  const scored = (options || []).map((j, i) => ({ j, i, s: lw.filter((w) => j.haystack.includes(w)).length }));
  scored.sort((a, b) => b.s - a.s || a.i - b.i);
  return scored.slice(0, limit).map((x) => x.j);
}
